"use client";

import Link from "next/link";
import { usePathname } from "next/navigation";
import { useSession } from "next-auth/react";
import { useState, useRef, useEffect } from "react";
import { FREE_TOOLS } from "@/config/freeTools";
import { toolPath } from "@/libs/peeqdPaths";
import { PeeqdToolContent, PEEQD_HOME_NAV } from "@/config/peeqdContent";
import { PEEQD_LEFT_RAIL_ENABLED } from "@/config/peeqdNav";
import { PeeqdLogoIcon, ToolIcon } from "./icons";
import { useAuthModal } from "@/contexts/AuthModalContext";
import { signOutToHome } from "@/libs/redirectLoading";
import type { PeeqdTopbarUser } from "@/components/peeqd/tracking/types";

interface PeeqdTopbarProps {
  content: PeeqdToolContent;
  showToolPills?: boolean;
  initialUser?: PeeqdTopbarUser | null;
}

function openMobileSidebar() {
  if (typeof window !== "undefined" && (window as Window & { __toggleMobileSidebar?: () => void }).__toggleMobileSidebar) {
    (window as Window & { __toggleMobileSidebar?: () => void }).__toggleMobileSidebar!();
  }
}

function PeeqdLogo({ className = "" }: { className?: string }) {
  return (
    <Link href="/" className={`flex items-center gap-2 ${className}`}>
      <PeeqdLogoIcon className="h-8 w-8 shadow-soft" />
      <span className="font-display text-[18px] font-extrabold tracking-tight">Peeqd</span>
    </Link>
  );
}

function UserAvatar({
  name,
  image,
  className = "h-7 w-7",
}: {
  name?: string | null;
  image?: string | null;
  className?: string;
}) {
  const [imgFailed, setImgFailed] = useState(false);
  const initial = name?.trim()?.[0]?.toUpperCase() ?? "?";

  if (!image || imgFailed) {
    return (
      <div
        className={`grid ${className} place-items-center rounded-full grad-bg text-[11px] font-bold text-white`}
      >
        {initial}
      </div>
    );
  }

  return (
    // eslint-disable-next-line @next/next/no-img-element
    <img
      src={image}
      alt=""
      className={`${className} rounded-full object-cover`}
      referrerPolicy="no-referrer"
      onError={() => setImgFailed(true)}
    />
  );
}

export default function PeeqdTopbar({
  content,
  showToolPills = true,
  initialUser = null,
}: PeeqdTopbarProps) {
  const pathname = usePathname();
  const { data: session, status } = useSession();
  const { openAuthModal } = useAuthModal();
  const [menuOpen, setMenuOpen] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);

  const sessionUser = session?.user;
  const displayUser = sessionUser ?? initialUser;
  const isAuthenticated =
    (status === "authenticated" && !!sessionUser) ||
    (status === "loading" && !!initialUser);

  useEffect(() => {
    const handleClick = (e: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(e.target as Node)) {
        setMenuOpen(false);
      }
    };
    document.addEventListener("click", handleClick);
    return () => document.removeEventListener("click", handleClick);
  }, []);

  useEffect(() => {
    const handleKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") setMenuOpen(false);
    };
    document.addEventListener("keydown", handleKey);
    return () => document.removeEventListener("keydown", handleKey);
  }, []);

  const isActive = (slug: string) => pathname === toolPath(slug);
  const isHomepage = pathname === "/";
  const headerBg = isAuthenticated ? "bg-surface" : "bg-bg";
  const showToolPillsRow = PEEQD_LEFT_RAIL_ENABLED && showToolPills;

  return (
    <header
      className={
        isHomepage
          ? `z-30 ${headerBg}`
          : `sticky top-0 z-30 border-b border-line ${headerBg}`
      }
    >
      <div className="relative flex h-16 items-center px-4 sm:px-6">
        {/* Left */}
        <div className="z-10 flex items-center gap-6 lg:gap-8">
          {PEEQD_LEFT_RAIL_ENABLED ? (
            <button
              type="button"
              onClick={openMobileSidebar}
              className="grid h-9 w-9 place-items-center rounded-lg border border-line bg-surface lg:hidden"
              aria-label="Open menu"
            >
              <svg className="h-5 w-5 text-ink" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M4 6h16M4 12h16M4 18h16" />
              </svg>
            </button>
          ) : (
            <PeeqdLogo />
          )}

          <nav className="hidden items-center gap-1 md:flex" aria-label="Page sections">
            {PEEQD_HOME_NAV.map((item) => (
              <Link
                key={item.href}
                href={item.href}
                className="rounded-lg px-3 py-2 text-[13px] font-semibold text-muted transition hover:text-ink"
              >
                {item.label}
              </Link>
            ))}
          </nav>
        </div>

        {/* Center - only when left rail is enabled (sidebar owns logo on desktop) */}
        {PEEQD_LEFT_RAIL_ENABLED && (
          <div className="pointer-events-none absolute inset-x-0 flex items-center justify-center px-16 sm:px-20">
            <PeeqdLogo className="pointer-events-auto lg:hidden" />
            <div className="pointer-events-auto hidden items-center gap-2 text-[13px] text-muted lg:flex">
              {pathname === "/" ? (
                <span className="font-semibold text-ink">Home</span>
              ) : (
                <>
                  <Link href="/" className="transition hover:text-ink">
                    Home
                  </Link>
                  <span className="text-line">/</span>
                  <span className="font-semibold text-ink">{content.crumb}</span>
                </>
              )}
            </div>
          </div>
        )}

        {/* Right */}
        <div className="relative z-10 ml-auto flex items-center gap-2 sm:gap-3">
          {isAuthenticated ? (
            <div className="relative" ref={menuRef}>
              <button
                type="button"
                onClick={(e) => {
                  e.stopPropagation();
                  setMenuOpen(!menuOpen);
                }}
                className="flex items-center gap-2 rounded-full border border-line bg-surface py-1 pl-1 pr-2.5 transition hover:shadow-soft"
                aria-haspopup="true"
                aria-expanded={menuOpen}
              >
                <UserAvatar name={displayUser?.name} image={displayUser?.image} />
                <span className="hidden text-[13px] font-semibold sm:block">
                  {displayUser?.name?.split(" ")[0] ?? "Account"}
                </span>
                <svg className="h-4 w-4 text-muted" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="m6 9 6 6 6-6" />
                </svg>
              </button>

              {menuOpen && (
                <div className="absolute right-0 mt-2 w-52 rounded-2xl border border-line bg-surface p-1.5 text-[13.5px] shadow-card">
                  <div className="mb-1 border-b border-line px-3 py-2">
                    <p className="font-semibold leading-tight">{displayUser?.name ?? "Account"}</p>
                    <p className="text-[12px] text-muted">{displayUser?.email}</p>
                  </div>
                  <Link href="/account" className="flex items-center gap-2.5 rounded-xl px-3 py-2 transition hover:bg-rose-softer" onClick={() => setMenuOpen(false)}>
                    Account
                  </Link>
                  <Link href="/pricing" className="flex items-center gap-2.5 rounded-xl px-3 py-2 transition hover:bg-rose-softer" onClick={() => setMenuOpen(false)}>
                    Billing
                  </Link>
                  <Link href="/contact" className="flex items-center gap-2.5 rounded-xl px-3 py-2 transition hover:bg-rose-softer" onClick={() => setMenuOpen(false)}>
                    Support
                  </Link>
                  <div className="my-1 border-t border-line" />
                  <button
                    type="button"
                    onClick={() => signOutToHome()}
                    className="flex w-full items-center gap-2.5 rounded-xl px-3 py-2 text-rose-deep transition hover:bg-rose-softer"
                  >
                    Log out
                  </button>
                </div>
              )}
            </div>
          ) : (
            <div className="flex items-center gap-3 sm:gap-4">
              <button
                type="button"
                onClick={() => openAuthModal()}
                className="text-[13px] font-semibold text-ink transition hover:text-rose-deep"
              >
                Log In
              </button>
              <button
                type="button"
                onClick={() => openAuthModal()}
                className="rounded-full grad-bg px-4 py-2 text-[13px] font-semibold text-white shadow-soft transition hover:opacity-95 active:scale-[.99]"
              >
                Sign Up
              </button>
            </div>
          )}
        </div>
      </div>

      {showToolPillsRow && (
        <div className={`lg:hidden ${headerBg}${isHomepage ? "" : " border-t border-line"}`}>
          <div className="no-bar flex gap-2 overflow-x-auto px-4 py-2.5">
            {FREE_TOOLS.filter((t) => t.platform === "instagram").map((tool) => (
              <Link
                key={tool.id}
                href={toolPath(tool.slug)}
                data-active={isActive(tool.slug) ? "true" : "false"}
                className="pill flex shrink-0 items-center gap-1.5 rounded-full border border-line bg-surface px-3.5 py-1.5 text-[13px] font-semibold"
              >
                <ToolIcon icon={tool.icon} className="h-[15px] w-[15px]" />
                {tool.shortTitle}
              </Link>
            ))}
          </div>
        </div>
      )}
    </header>
  );
}
